import { sectionRenderer } from '@theme/section-renderer';

/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in session storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 12;

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== productId);
    viewedProducts.unshift(productId);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
    
    // Dispatch custom event for same-window updates
    window.dispatchEvent(new CustomEvent('viewedProducts:updated', {
      detail: { productId: productId }
    }));
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
  }

  /**
   * Retrieves the list of recently viewed products from session storage.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    return JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
  }
}

// Make it available globally for non-module scripts
if (typeof window !== 'undefined') {
  window.RecentlyViewed = RecentlyViewed;
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define(
    'recently-viewed-products',
    class RecentlyViewedProducts extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        // Load immediately when element is connected
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.init());
        } else {
          // Small delay to ensure DOM is ready
          setTimeout(() => this.init(), 100);
        }
      }

      init() {
        const items = RecentlyViewed.getProducts();
        const productId = this.dataset.productId ? parseInt(this.dataset.productId) : null;
        const productsToShow = parseInt(this.dataset.productsToShow || '8');
        const sectionId = this.dataset.sectionId;

        if (!sectionId) {
          return;
        }

        if (!items || items.length === 0) {
          const carousel = document.getElementById('pf-' + sectionId + '-wrap');
          const emptyMsg = document.getElementById('pf-' + sectionId + '-empty');
          if (carousel) carousel.style.display = 'none';
          if (emptyMsg) emptyMsg.style.display = '';
          return;
        }

        // Exclude current product if on product page
        let filteredItems = items;
        if (productId) {
          const productIdStr = productId.toString();
          if (items.includes(productIdStr)) {
            filteredItems = items.filter((id) => id !== productIdStr);
          }
        }

        // Limit products
        const limitedItems = filteredItems.slice(0, productsToShow);

        if (limitedItems.length === 0) {
          const carousel = document.getElementById('pf-' + sectionId + '-wrap');
          const emptyMsg = document.getElementById('pf-' + sectionId + '-empty');
          if (carousel) carousel.style.display = 'none';
          if (emptyMsg) emptyMsg.style.display = '';
          return;
        }

        // Use sectionRenderer like predictive-search does
        const searchUrl = new URL(window.location.origin + '/search');
        const queryParams = limitedItems.map((item) => 'id:' + item).join(' OR ');
        searchUrl.searchParams.set('q', queryParams);
        searchUrl.searchParams.set('resources[type]', 'product');
        
        sectionRenderer.getSectionHTML(sectionId, false, searchUrl)
          .then((responseText) => {
            // Section Rendering API returns the full section HTML
            const parser = new DOMParser();
            const doc = parser.parseFromString(responseText, 'text/html');
            
            // Try to find the section content
            let sectionContent = doc.querySelector('[section-id="' + this.dataset.sectionId + '"]');
            
            // If not found, try to find it by section class
            if (!sectionContent) {
              sectionContent = doc.querySelector('.section[section-id="' + this.dataset.sectionId + '"]');
            }
            
            // If still not found, the response might be just the inner HTML
            if (!sectionContent && responseText.trim()) {
              // Create a wrapper to parse
              const wrapper = document.createElement('div');
              wrapper.innerHTML = responseText;
              sectionContent = wrapper.querySelector('[section-id="' + this.dataset.sectionId + '"]');
            }

            const container = document.querySelector('[section-id="' + this.dataset.sectionId + '"]');
            if (!container) {
              return;
            }

            if (sectionContent && sectionContent.innerHTML.trim().length) {
              // Check if there are products in the loaded content
              const productList = sectionContent.querySelector('#pf-' + this.dataset.sectionId + '-scroller') ||
                                 sectionContent.querySelector('.product-grid--simple') ||
                                 sectionContent.querySelector('ul.product-grid');
              
              const scroller = document.getElementById('pf-' + this.dataset.sectionId + '-scroller');
              
              if (productList && scroller) {
                // Show carousel, hide empty message
                const carousel = document.getElementById('pf-' + this.dataset.sectionId + '-wrap');
                const emptyMsg = document.getElementById('pf-' + this.dataset.sectionId + '-empty');
                if (carousel) carousel.style.display = '';
                if (emptyMsg) emptyMsg.style.display = 'none';

                // Update only the product list
                scroller.innerHTML = productList.innerHTML;
                
                // Re-run the script to initialize carousel if it exists in container
                const scripts = container.querySelectorAll('script');
                if (scripts.length === 0) {
                  // Script might be in the loaded content, try to execute inline
                  const inlineScript = sectionContent.querySelector('script');
                  if (inlineScript && inlineScript.textContent) {
                    try {
                      // Use Function instead of eval for better safety
                      const scriptFn = new Function(inlineScript.textContent);
                      scriptFn();
                    } catch (e) {
                      // Silent fail
                    }
                  }
                } else {
                  scripts.forEach((script) => {
                    const newScript = document.createElement('script');
                    if (script.src) {
                      newScript.src = script.src;
                      newScript.defer = script.defer;
                      document.body.appendChild(newScript);
                    } else {
                      newScript.textContent = script.textContent;
                      document.body.appendChild(newScript);
                      setTimeout(() => newScript.remove(), 0);
                    }
                  });
                }
              } else {
                // No products found, show empty message
                const carousel = document.getElementById('pf-' + this.dataset.sectionId + '-wrap');
                const emptyMsg = document.getElementById('pf-' + this.dataset.sectionId + '-empty');
                if (carousel) carousel.style.display = 'none';
                if (emptyMsg) emptyMsg.style.display = '';
              }
            } else {
              // No content, show empty message
              const carousel = document.getElementById('pf-' + this.dataset.sectionId + '-wrap');
              const emptyMsg = document.getElementById('pf-' + this.dataset.sectionId + '-empty');
              if (carousel) carousel.style.display = 'none';
              if (emptyMsg) emptyMsg.style.display = '';
            }
          })
          .catch((e) => {
            // On error, show empty message
            const carousel = document.getElementById('pf-' + this.dataset.sectionId + '-wrap');
            const emptyMsg = document.getElementById('pf-' + this.dataset.sectionId + '-empty');
            if (carousel) carousel.style.display = 'none';
            if (emptyMsg) emptyMsg.style.display = '';
          });
      }

    }
  );
}
